/**
 * `/gallery` — the public directory of published recipes (PLAN-GALLERY G3).
 *
 * An ordinary route inside the app shell, with **no guard of any kind**, and that
 * absence is the design: a signed-out visitor is a first-class reader here. The
 * shell does not gate on a session either (`components/app-shell.tsx` waits on
 * the *workspace* hydrating, which happens for a guest exactly as it does for an
 * account), so nothing above this file has to be taught about the case.
 *
 * Contrast `/s/:token`, which renders *outside* the shell: that page is the end
 * of a link somebody was sent, and this one is a room in the product.
 *
 * ## The whole view is in the URL
 *
 * The tab was first: the page has two of them — the directory and the brewer's
 * own listings — and which one is showing is `?tab=mine` rather than component
 * state, so it is an address other things can point at. The recipe header's
 * Published chip leads here, and `/listings`, which used to be a page of its
 * own, redirects onto it.
 *
 * The three browse filters joined it (PLAN-UX-PASS E3). They lived in the
 * panel's own state, which held them across a tab flip and lost them on the one
 * navigation a reader makes all day: a card opens `/g/:entryId`, which renders
 * *outside* the shell, so following one is a full document load and coming back
 * was an unfiltered grid sorted by Newest. In the URL they survive that trip —
 * the card carries them and the published page's back link hands them back — and
 * "the saisons in the gallery" becomes a link somebody can send, which for a
 * page built as a public landing surface (PLAN-MARKETING B3) is the half of the
 * state that most wanted addressing.
 *
 * Absent means the default and is never written back out — no `?tab=browse`, no
 * `?sort=newest`, and `undefined` rather than `null` for every absent value,
 * which is the convention every list route here follows (see
 * `routes/recipes/index.tsx`, and `routes/ingredients.tsx` for the `?id=null`
 * that taught it). Page depth stays out: "Show more" is a local gesture, and a
 * link to page three of a sorted feed is stale by the next publish.
 */

import { styles as bjcpStyles } from '@beerwright/catalog';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GalleryPage } from '@/components/gallery/gallery-page';
import type { GalleryView } from '@/gallery/protocol';
import { GALLERY_SORTS } from '@/gallery/protocol';
import { parseSearchEnum, parseSearchString } from '@/lib/search-params';

/**
 * What `/gallery` reads out of its own URL.
 *
 * {@link GalleryView} exactly, because a card carries the same four values into
 * the published page and hands them back on the way home — one shape, so the
 * two directions cannot disagree about what a gallery view is.
 */
export type GallerySearch = GalleryView;

/** Every style id this build knows, for {@link parseStyleId}. */
const STYLE_IDS: ReadonlySet<string> = new Set(bjcpStyles.map((style) => style.id));

/**
 * A style the catalog actually has, or `undefined`.
 *
 * The same treatment `?sort=` gets, and for a case that will arrive on its own:
 * a style id renamed or retired between BJCP revisions leaves old shared links
 * naming one this build cannot find. Passed straight through, that id would
 * filter the grid to nothing while the filter button read "Every style" — an
 * empty gallery whose controls claim to be off. Dropped here, the link opens the
 * gallery it was meant to be, one filter short.
 */
function parseStyleId(value: unknown): string | undefined {
  const id = parseSearchString(value);
  return id !== undefined && STYLE_IDS.has(id) ? id : undefined;
}

export const Route = createFileRoute('/gallery')({
  validateSearch: (
    search: {
      readonly tab?: unknown;
      readonly q?: unknown;
      readonly style?: unknown;
      readonly sort?: unknown;
    } & SearchSchemaInput,
  ): GallerySearch => ({
    tab: parseSearchEnum(search.tab, ['mine'] as const),
    q: parseSearchString(search.q),
    style: parseStyleId(search.style),
    sort: parseSearchEnum(search.sort, GALLERY_SORTS),
  }),
  component: GalleryRoute,
});

function GalleryRoute() {
  const view = Route.useSearch();
  // Scoped `from`, like `/recipes`: an unscoped `useNavigate` types `search`
  // against every registered route at once and cannot know this one's shape.
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <GalleryPage
      // The search *is* the view: the page reads these on every render rather
      // than seeding from them, so a Back that only changes the search params —
      // which does not remount this route — moves the grid with it.
      view={view}
      // A push, not a replace: choosing a tab is a decision, so Back undoes it.
      onTabChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, tab: next === 'browse' ? undefined : 'mine' }),
        });
      }}
      // `replace: true`, as `/recipes` does for its box: a settled keystroke
      // narrows the same search rather than starting a new one, so Back undoes
      // the whole search instead of walking back through "s", "sa", "sai". The
      // page debounces, so this is one navigation per word, not per letter.
      onSearchChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, q: next === '' ? undefined : next }),
          replace: true,
        });
      }}
      // A style or a sort is a press, and a press is a decision: both push.
      onStyleChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, style: next }) });
      }}
      onSortChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, sort: next === 'newest' ? undefined : next }),
        });
      }}
    />
  );
}
